import React, { useState } from "react";
import svg156 from "../../../../assets/svg/svg156.svg";
import svg155 from "../../../../assets/svg/svg155.svg";
import svg157 from "../../../../assets/svg/svg157.svg";
import svg158 from "../../../../assets/svg/svg158.svg";
import PhoneInput from "react-phone-input-2";
import { Form, Input, InputNumber, Radio, Spin } from "antd";
import PaymentSuccessModal from "../../../../components/PaymentSuccessModal";
import SpiningModal from "../../../../components/SpiningModal";
import { fetchData, save } from "../../../../features/auth/authSlice";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { normalizeDateToLocal } from "../../../../utils";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

async function getCaptchaToken(action) {
  return new Promise((resolve, reject) => {
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(SITE_KEY, { action });
        resolve(token);
      } catch (e) {
        reject(e);
      }
    });
  });
}

function PaymentMethod({ form, accountNumber, company, handleModalCancel }) {
  const [selectedMethod, setSelectedMethod] = useState("mpesa");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [isManualChecking, setIsManualChecking] = useState(false);
  const [lastPaymentParams, setLastPaymentParams] = useState(null);
  const [isFailed, setIsFailed] = useState(false);
  const [failureMessage, setFailureMessage] = useState(null);
  const dispatch = useDispatch();
  const url = "api/v1/public/query-paybill-payment-status";
  const { saving } = useSelector((state) => state.auth);
  const { loading, data } = useSelector((state) => ({
    loading: state.fetch.loading,
    data: state.fetch.data[url] || [],
  }));

  const handleSubmit = async () => {
    try {
      if (selectedMethod === "mpesa") {
        await form.validateFields(["phoneNumber", "editableAmount"]);
        const phoneNumber = form.getFieldValue("phoneNumber");
        const editableAmount = form.getFieldValue("editableAmount");

        let captchaToken;
        try {
          captchaToken = await getCaptchaToken("WATER_PAYMENT");
        } catch {
          toast.error("reCAPTCHA not ready. Please try again.");
          return;
        }

        const res = await dispatch(
          save({
            url: "api/v1/public/create-paybill-payment",
            upPbId: company.pbId,
            upAccountNumber: accountNumber,
            upPhoneNumber: phoneNumber,
            upAmount: editableAmount,
            upPaymentType: "MOBILE_MONEY",
            upPaymentMode: "MPESA_B2C",
            upType:"WATER",
            captchaToken,
          })
        );

        if (res?.payload?.success) {
          await toast.success(res?.payload?.messages?.message);

          setIsPolling(true);
          setIsTimedOut(false);
          setCurrentAttempt(0);
          const maxAttempts = 6;
          let attempts = 0;
          let pollSuccess = false;

          // Store payment params for manual check
          const paymentParams = {
            ...res?.payload?.data?.result,
            accountNumber,
            phoneNumber,
            editableAmount,
          };
          setLastPaymentParams(paymentParams);

          const pollPaymentStatus = async () => {
            while (attempts < maxAttempts && !pollSuccess) {
              attempts++;
              setCurrentAttempt(attempts);

              const res1 = await dispatch(
                fetchData({
                  url: url,
                  billRefNumber: paymentParams?.upBillRefNumber,
                  transDate: normalizeDateToLocal(new Date()),
                  transAmount: paymentParams?.upAmount,
                })
              );
              const paymentRecord = res1?.payload?.data?.[0];
              if (paymentRecord?.upStatus === 'FAILED') {
                setIsPolling(false);
                setFailureMessage(paymentRecord?.upDesc || null);
                setIsFailed(true);
                break;
              } else if (res1?.payload?.data && res1?.payload?.data?.length > 0 && paymentRecord?.upStatus === 'COMPLETED') {
                pollSuccess = true;
                setIsPolling(false);
                setIsTimedOut(false);
                await toast.success("Payment confirmed successfully!");
                setPaymentData({
                  amount: paymentRecord?.transAmount || paymentParams?.upAmount,
                  transactionId: paymentRecord?.transId || `TXN-${Date.now()}`,
                  paymentMethod: `M-Pesa (${phoneNumber})`,
                  date: normalizeDateToLocal(new Date()),
                  purpose: "Water Bills",
                });
                setShowSuccessModal(true);
                break;
              } else {
                if (attempts < maxAttempts) {
                  await new Promise((resolve) => setTimeout(resolve, 10000));
                }
              }
            }

            if (!pollSuccess) {
              setIsPolling(false); // Stop the spinner on the button
              setIsTimedOut(true);
              // Don't close the modal, show timeout state with manual check button
            }
          };

          await pollPaymentStatus();
        } else {
          toast.error(res?.payload?.messages?.message);
        }
      } else {
        await form.validateFields([
          "cardNumber",
          "expiryDate",
          "cvv",
          "nameOnCard",
        ]);
        const cardNumber = form.getFieldValue("cardNumber");

        // Simulate payment processing
        // In real implementation, you would call your payment API here

        // After successful payment, prepare data and show success modal
        setPaymentData({
          amount: 1208.0, // Get this from your form or state
          transactionId: `TXN-${Date.now()}`,
          paymentMethod: `**** ${String(cardNumber).slice(-4)}`,
          date: new Date()
            .toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
            .toUpperCase(),
          purpose: "Electric Tokens",
        });
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.log("Validation failed:", error);
    }
  };

  const handleDownloadReceipt = () => {
    console.log("Downloading receipt for:", paymentData);
    // Add your receipt download logic here
    // Example: Generate PDF, trigger download, etc.
  };

  const handleReturnHome = async () => {
    await setShowSuccessModal(false);
    await setIsPolling(false);
    await setIsTimedOut(false);
    await setIsFailed(false);
    await setFailureMessage(null);
    await setCurrentAttempt(0);
    await handleModalCancel();
    await form.resetFields();
  };

  const handleManualCheck = async () => {
    if (!lastPaymentParams) return;

    setIsManualChecking(true);

    try {
      const res1 = await dispatch(
        fetchData({
          url: url,
          billRefNumber: lastPaymentParams?.upBillRefNumber,
          transDate: normalizeDateToLocal(new Date()),
          transAmount: lastPaymentParams?.upAmount,
        })
      );

      const paymentRecord = res1?.payload?.data?.[0];
      if (paymentRecord?.upStatus === 'FAILED') {
        setIsTimedOut(false);
        setFailureMessage(paymentRecord?.upDesc || null);
        setIsFailed(true);
      } else if (res1?.payload?.data && res1?.payload?.data?.length > 0 && paymentRecord?.upStatus === 'COMPLETED') {
        setIsPolling(false);
        setIsTimedOut(false);
        toast.success("Payment confirmed successfully!");
        setPaymentData({
          amount: paymentRecord?.transAmount || lastPaymentParams?.upAmount,
          transactionId: paymentRecord?.transId || `TXN-${Date.now()}`,
          paymentMethod: `M-Pesa (${lastPaymentParams?.upPhoneNumber})`,
          date: normalizeDateToLocal(new Date()),
          purpose: "Water Bills",
        });
        setShowSuccessModal(true);
      } else {
        toast.error("No transactions found in the last 2 minutes matching the criteria");
      }
    } catch (error) {
      toast.error("Failed to check payment status.");
    } finally {
      setIsManualChecking(false);
    }
  };

  const handleClosePollingModal = () => {
    setIsPolling(false);
    setIsTimedOut(false);
    setIsFailed(false);
    setFailureMessage(null);
    setCurrentAttempt(0);
  };

  return (
    <div className="rounded-[0.75rem] bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.05)] p-[0.75rem] mt-[1rem]">
      <div className="flex items-center gap-[0.5rem] mb-[1rem]">
        <div className="w-[1.5rem] h-[1.5rem] rounded-[0.375rem] bg-[#F1F5F9] flex items-center justify-center">
          <img src={svg155} alt="svg155" />
        </div>
        <span className="text-[#0F172A] font-[500] font-lexendS text-[1rem] leading-[1.5rem]">
          Select Payment Method
        </span>
      </div>

      <Radio.Group
        value={selectedMethod}
        onChange={(e) => setSelectedMethod(e.target.value)}
        className="w-full"
      >
        {/* M-Pesa Option */}
        <div
          className={`mt-[0.5rem] cursor-pointer border rounded-[0.5rem] py-[0.6875rem] px-[0.8125rem] w-full ${
            selectedMethod === "mpesa"
              ? "border-[#2F9461] bg-[#F0FDF4]"
              : "border-[#E2E8F0] bg-white"
          }`}
          onClick={() => setSelectedMethod("mpesa")}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-[0.75rem]">
              <img src={svg156} alt="svg156" className="w-[2rem] h-[2rem]" />
              <div className="flex flex-col font-lexendS">
                <span className="text-[#000] text-[0.875rem] font-[500] leading-[1.25rem]">
                  Safaricom (M‑Pesa)
                </span>
                <span className="text-[#7A7493] text-[0.75rem] font-[400] leading-[1rem]">
                  Mobile money
                </span>
              </div>
            </div>
            <Radio value="mpesa" />
          </div>
        </div>

        {/* Card Option */}
        <div
          className={`mt-[0.5rem] cursor-pointer border rounded-[0.5rem] py-[0.6875rem] px-[0.8125rem] w-full ${
            selectedMethod === "card"
              ? "border-[#2F9461] bg-[#F0FDF4]"
              : "border-[#E2E8F0] bg-white"
          }`}
          onClick={() => setSelectedMethod("card")}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-[0.75rem]">
              <div className="w-[2rem] h-[2rem] flex items-center justify-center rounded-[0.375rem] bg-[#0F172A]">
                <img src={svg158} alt="card" />
              </div>
              <div className="flex flex-col font-lexendS">
                <span className="text-[#000] text-[0.875rem] font-[500] leading-[1.25rem]">
                  Card
                </span>
                <span className="text-[#7A7493] text-[0.75rem] font-[400] leading-[1rem]">
                  Visa / Mastercard
                </span>
              </div>
            </div>
            <div className="flex items-center gap-[0.5rem]">
              <div className="flex items-center gap-[0.38rem]">
                <div className="px-[0.375rem] py-[0.125rem] rounded-[0.25rem] bg-[#1A1F71] text-white font-lexendS font-[600] text-[0.5625rem] leading-[0.9375rem]">
                  VISA
                </div>
                <div className="rounded-[0.25rem] bg-white shadow-[0_0_0_1px_#E2E8F0] py-[0.125rem] px-[0.25rem]">
                  <img src={svg157} alt="mastercard" className="h-[0.75rem]" />
                </div>
              </div>
              <Radio value="card" />
            </div>
          </div>
        </div>
      </Radio.Group>

      {/* M-Pesa Phone Number Field */}
      {selectedMethod === "mpesa" && (
        <div className="mt-[1rem]">
          <Form.Item
            name="phoneNumber"
            label={"M‑Pesa Phone Number"}
            rules={[
              {
                required: true,
                message:
                  "Please enter a valid phone number in the format +254XXXXXXXXX.",
              },
              {
                validator: (_, value) => {
                  if (value && value.length === 12) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Phone number must be exactly 10 characters")
                  );
                },
              },
            ]}
          >
            <PhoneInput
              country="ke"
              countryCodeEditable={false}
              onKeyDown={(e) => {
                const input = e.target;
                const value = input.value?.replace(/[^0-9]/g, "");

                if (e.key === "0" && (!value || value === "254")) {
                  e.preventDefault();
                  return;
                }
                const allowedKeys = [
                  "Backspace",
                  "Delete",
                  "ArrowLeft",
                  "ArrowRight",
                  "Tab",
                ];
                if (
                  value &&
                  value.length >= 12 &&
                  !allowedKeys.includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              className="input"
            />
          </Form.Item>
        </div>
      )}

      {/* Card Payment Details */}
      {selectedMethod === "card" && (
        <div className="mt-[1rem]">
          <div className="text-[#0F172A] text-[0.875rem] font-[500] leading-[1.25rem] font-lexendS mb-[1rem]">
            Card Payment Details
          </div>

          <Form.Item
            name="cardNumber"
            label={"Card Number"}
            rules={[{ required: true, message: "Invalid card number." }]}
          >
            <InputNumber
              parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
              suffix={
                <div className="flex items-center gap-[0.25rem]">
                  <div className="px-[0.375rem] py-[0.125rem] rounded-[0.25rem] bg-[#1A1F71] text-white font-lexendS font-[600] text-[0.5625rem] leading-[0.9375rem]">
                    VISA
                  </div>
                  <div className="rounded-[0.25rem] bg-white shadow-[0_0_0_1px_#E2E8F0] py-[0.125rem] px-[0.25rem]">
                    <img
                      src={svg157}
                      alt="mastercard"
                      className="h-[0.75rem]"
                    />
                  </div>
                </div>
              }
              className="input"
            />
          </Form.Item>

          <div className="grid grid-cols-2 gap-[0.75rem]">
            <Form.Item
              name="expiryDate"
              label={"Expiry Date"}
              rules={[
                {
                  required: true,
                  message: "Enter a valid expiry date (MM/YY).",
                },
              ]}
            >
              <Input placeholder="MM / YY" className="input" />
            </Form.Item>

            <Form.Item
              name="cvv"
              label={"CVV"}
              rules={[
                { required: true, message: "Enter a valid 3-digit CVV." },
              ]}
            >
              <InputNumber
                parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                placeholder="123"
                maxLength={3}
                suffix={<span className="text-[#94A3B8] text-[1rem]">?</span>}
                className="input"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="nameOnCard"
            label={"Name on Card"}
            rules={[
              {
                required: true,
                message: "Please enter the cardholder's name.",
              },
            ]}
          >
            <Input placeholder="Full Name" className="input" />
          </Form.Item>
        </div>
      )}

      <button
        type="button"
        disabled={saving}
        onClick={handleSubmit}
        className="cstm-btn !bg-[#2F9461] !text-white"
      >
        {saving || isPolling ? <Spin /> : "Proceed to Pay"}
      </button>

      {/* Spinning Modal for Polling */}
      <SpiningModal
        open={isPolling || isTimedOut || isFailed}
        handleCancel={handleClosePollingModal}
        currentAttempt={currentAttempt}
        maxAttempts={6}
        onManualCheck={handleManualCheck}
        isTimedOut={isTimedOut}
        isFailed={isFailed}
        failureMessage={failureMessage}
        isChecking={isManualChecking}
      />

      {/* Payment Success Modal */}
      {paymentData && (
        <PaymentSuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          amount={paymentData.amount}
          transactionId={paymentData.transactionId}
          paymentMethod={paymentData.paymentMethod}
          date={paymentData.date}
          purpose={paymentData.purpose}
          currency="Ksh"
          confirmationMessage="Your service provider will send you a confirmation by SMS or email."
          onDownloadReceipt={handleDownloadReceipt}
          onReturnHome={handleReturnHome}
          showDownloadButton={true}
          showReturnButton={true}
          supportText="Need help?"
          supportLink="Contact our support team"
        />
      )}
    </div>
  );
}

export default PaymentMethod;
